import {
  BreakEventStatus,
  CalendarSyncBreak,
  CalendarSyncPair,
  SlotRebuildStatus,
  SlotRebuildTrigger,
} from 'services/selfSchedulingServices';

/**
 * MM-1198: what the Sync Jobs main row shows for a location + provider - the run that is going on now, or the latest
 * finished one when nothing waits.
 */
export interface CurrentRun {
  trigger: SlotRebuildTrigger | null;
  requestedDate: number | null;
  startedDate: number | null;
  completedDate: number | null;
  status: SlotRebuildStatus | null;
  /** "Retries x/y" of a full rebuild, null if none (a break rebuild is not retried). */
  retryText: string | null;
  nextRetryDate: number | null;
  /** The cause of error, null if none. */
  message: string | null;
}

/**
 * MM-1198: every stored break of a location + provider (a copy, safe to sort) - each one triggers a full rebuild of
 * the location + provider, so breaks are no longer split by profile.
 */
export const pairBreaks = (pair: CalendarSyncPair): CalendarSyncBreak[] => [...(pair.breaks ?? [])];

/**
 * MM-1198: only the dates of the run itself - a Pending run has not started, so the last run's Started and
 * Completed are not shown; an In progress run shows its own Started and no Completed until it finishes.
 */
const withRunDates = (run: CurrentRun): CurrentRun => {
  if (run.status === 'PENDING') {
    return { ...run, startedDate: null, completedDate: null };
  }
  if (run.status === 'IN_PROGRESS') {
    return { ...run, completedDate: null };
  }
  return run;
};

/** MM-1198: the full rebuild of a location + provider as a run. */
const rebuildRun = (pair: CalendarSyncPair): CurrentRun => withRunDates({
  trigger: pair.rebuildTrigger,
  requestedDate: pair.requestedDate,
  startedDate: pair.rebuildStartedDate,
  completedDate: pair.lastCompletedDate,
  status: pair.rebuildStatus,
  // only once a retry was used - no "Retries 0/3"
  retryText: pair.retryLimit && pair.retryCount > 0 ? `Retries ${pair.retryCount}/${pair.retryLimit}` : null,
  nextRetryDate: pair.nextRetryDate,
  message: pair.rebuildMessage,
});

/**
 * MM-1198: a break as a run - a break rebuild is not retried, so a failed one shows as Failed with its error message
 * as the cause of error, and no retry data.
 */
const breakRun = (item: CalendarSyncBreak): CurrentRun => {
  const statuses: Record<BreakEventStatus, SlotRebuildStatus> = {
    INPROGRESS: 'IN_PROGRESS',
    PENDING: 'PENDING',
    FAILURE: 'FAILED',
    COMPLETED: 'COMPLETED',
    SUPERSEDED: 'COMPLETED',
    IGNORED: 'COMPLETED',
    EXPIRED: 'COMPLETED',
  };
  return withRunDates({
    trigger: 'BREAK_UPDATE',
    requestedDate: item.receivedDate,
    startedDate: item.startedDate,
    completedDate: item.completedDate,
    status: statuses[item.status],
    retryText: null,
    nextRetryDate: null,
    message: item.status === 'FAILURE' ? item.errorMessage : null,
  });
};

/**
 * MM-1198: the run the Sync Jobs main row shows for a location + provider, so it is never a stale completed run
 * while something waits:
 * 1. in progress - the full rebuild, else the break being processed;
 * 2. pending - the full rebuild or break that was queued first (it is picked next; breaks are processed oldest
 *    first);
 * 3. otherwise the latest finished one - the full rebuild or the latest processed break, by when it finished.
 */
export const currentRun = (pair: CalendarSyncPair): CurrentRun => {
  const rebuild = rebuildRun(pair);
  const breaks = pairBreaks(pair).sort((a, b) => a.eventId - b.eventId);
  if (pair.rebuildStatus === 'IN_PROGRESS') {
    return rebuild;
  }
  const running = breaks.find((item) => item.status === 'INPROGRESS');
  if (running) {
    return breakRun(running);
  }
  const nextBreak = breaks.find((item) => item.status === 'PENDING');
  if (pair.rebuildStatus === 'PENDING' && pair.requestedDate) {
    return nextBreak && (nextBreak.receivedDate ?? Infinity) < pair.requestedDate ? breakRun(nextBreak) : rebuild;
  }
  if (nextBreak) {
    return breakRun(nextBreak);
  }
  const lastBreak = breaks
    .filter((item) => (item.status === 'COMPLETED' || item.status === 'FAILURE') && item.completedDate)
    .sort((a, b) => (b.completedDate ?? 0) - (a.completedDate ?? 0))[0];
  const rebuildFinished = pair.lastCompletedDate ?? pair.rebuildStartedDate ?? 0;
  return lastBreak && (lastBreak.completedDate ?? 0) > rebuildFinished ? breakRun(lastBreak) : rebuild;
};
